/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'

/**
 * Import async components.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-snippet': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-snippet' */'~async/accordion-snippet/accordion-snippet') }),
    'account-garage': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.account-garage' */'~async/account-garage/account-garage') }),
    'brand-block': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.brand-block' */'~async/brand-block/brand-block') }),
    'breadcrumbs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.breadcrumbs' */'~async/breadcrumbs/breadcrumbs') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'collection-doorways': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-doorways' */'~async/collection-doorways/collection-doorways') }),
    'columns-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.columns-section' */'~async/columns-section/columns-section') }),
    'comparison-table': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.comparison-table' */'~async/comparison-table/comparison-table') }),
    'compatibility-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.compatibility-drawer' */'~async/compatibility-drawer/compatibility-drawer') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-online-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-online-products' */'~async/customers/online-products/customers-online-products') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-product-activation': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-product-activation' */'~async/customers/product-activation/customers-product-activation') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-register-b2b': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register-b2b' */'~async/customers/register-b2b/customers-register-b2b') }),
    'customers-register-online-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register-online-products' */'~async/customers/register-online-products/customers-register-online-products') }),
    'customers-register-qr-redirect': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register-qr-redirect' */'~async/customers/register-qr-redirect/customers-register-qr-redirect') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'footer-usps': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.footer-usps' */'~async/footer-usps/footer-usps') }),
    'garage-modal': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.garage-modal' */'~async/garage-modal/garage-modal') }),
    'gorgias-contact-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.gorgias-contact-form' */'~async/gorgias-contact-form/gorgias-contact-form') }),
    'heading-copy': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.heading-copy' */'~async/heading-copy/heading-copy') }),
    'hero-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-banner' */'~async/hero-banner/hero-banner') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'multi-country-switcher': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-country-switcher' */'~async/multi-country-switcher/multi-country-switcher') }),
    'newsletter-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.newsletter-form' */'~async/newsletter-form/newsletter-form') }),
    'newsletter-sign-up': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.newsletter-sign-up' */'~async/newsletter-sign-up/newsletter-sign-up') }),
    'predictive-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.predictive-search' */'~async/predictive-search/predictive-search') }),
    'product-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-collections' */'~async/product-collections/product-collections') }),
    'promo-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promo-banner' */'~async/promo-banner/promo-banner') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'suggested-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.suggested-products' */'~async/suggested-products/suggested-products') }),
    'tabs-snippet': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.tabs-snippet' */'~async/tabs-snippet/tabs-snippet') }),
    'text-and-image': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-and-image' */'~async/text-and-image/text-and-image') }),
    'transition-height': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.transition-height' */'~async/transition-height/transition-height') }),
    'vehicle-finder': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.vehicle-finder' */'~async/vehicle-finder/vehicle-finder') }),
    'vehicle-finder-cta': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.vehicle-finder-cta' */'~async/vehicle-finder-cta/vehicle-finder-cta') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import GarageSync from '~global/core/garage-sync'
import Index from '~global/core/index'
import Navigation from '~global/site-header-group/navigation/navigation'
import NavigationTiers from '~global/navigation-tiers/navigation-tiers'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'
import ProductCardSimple from '~global/product-card-simple/product-card-simple'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SearchBar from '~global/search-bar/search-bar'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'btn': Btn,
    'garage-sync': GarageSync,
    'index': Index,
    'navigation': Navigation,
    'navigation-tiers': NavigationTiers,
    'overlay': Overlay,
    'product-card': ProductCard,
    'product-card-simple': ProductCardSimple,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'search-bar': SearchBar,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    // canvas-global-object-end
  }
}

/**
 * Import web components.
 * - Must be manually updated with new async web components (components/async).
 * - Use component command import to update.
 */
// canvas-web-import-start
// canvas-web-import-end

/**
 * Import Vuex stores.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import GarageStore from '~stores/garage'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'garage': GarageStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    // canvas-stores-object-end
  }
}
